/**
 * This module exports Scalyr-defined pure functions for manipulating strings.
 */
import { PropertyName, PropertyPath } from 'lodash';
import isArray from 'lodash/isArray';
import { unparse } from 'papaparse';
import { string } from 'yup';

import * as K from 'common/constants';

/**
 * This function selects either single quotes or double quotes, then escapes all instances of that quote character
 * within a text by prepending backslash, and finally wraps the entire text with the selected quotes.
 *
 * The selection of single or double quotes is based on how many embedded quote characters need to be
 * escaped - an effort is made to minimize the number of escaped characters.
 *
 * For example, if the string CAT'S PAWS is provided, "CAT'S PAWS" will be returned.
 * If the string SO "WHAT"? is provided, 'SO "WHAT"?' will be returned.
 * And if SO "WHAT'S" UP is provided, 'SO "WHAT\'S" UP' will be returned.
 *
 * @param text               the string to be quoted
 * @param useDoubleQuotes    if true, force the use of double quotes to quote the string
 * @param noTrim             if true, do not trim string before quoting
 */
export const quote = (text: string, useDoubleQuotes = false, noTrim = false): string => {
  if (!text) {
    return "''";
  }

  const numSingleQuotes = (text.match(/'/g) || []).length;
  const numDoubleQuotes = (text.match(/"/g) || []).length;
  if (numSingleQuotes <= numDoubleQuotes && !useDoubleQuotes) {
    const escapedText = text.replace(/'/g, "\\'");
    return "'" + (noTrim ? escapedText : escapedText.trim()) + "'";
  }

  const escapedText = text.replace(/"/g, '\\"');
  return '"' + (noTrim ? escapedText : escapedText.trim()) + '"';
};

/**
 * Remove surrounding single or double quotes from a text, and then unescape all instances of that quote character
 * within the text. The first and last character must both be single quotes, or else they must both be double quotes.
 * If not, this function returns the original string.
 *
 * "\"" --> "
 * '\"' --> \"
 * "fo"o' --> "fo"o'
 *
 * For practical purposes, this is an inverse function of the quote function from this library.
 */
export const unquote = (text: string): string => {
  if (!text) {
    return '';
  }

  const first = text[0];
  const last = text[text.length - 1];
  const match = first === last && ["'", '"'].includes(first);
  if (!match) {
    return text;
  }
  const regex = new RegExp('\\\\' + first, 'g');

  return text
    .substring(1, text.length - 1)
    .trim()
    .replace(regex, first);
};

/**
 * Escape a string so that it's safe to create a regex out of via 'new RegExp()'.
 *
 * @param   regex   a string to use in a regex
 *
 * @returns         a regex-safe string
 */
export const regExEscape = (regex: string): string => regex.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string

/**
 * Escapes all backslashes by replacing all instances of \ with \\.
 */
export const escapeBackslash = (text: string): string => (text ? text.replace(/\\/g, '\\\\') : '');

/**
 * Unescapes all backslashes by replacing all instances of \\ with \.
 */
export const unescapeBackslash = (text: string): string => (text ? text.replace(/\\\\/g, '\\') : '');

/**
 * Converts a filter value to plain text by removing and unescaping quotes, and unescaping backslashes.
 *
 * @param   value    filter to convert
 * @returns plain text version of filter value
 */
export const filterValueToText = (value: string): string => unescapeBackslash(unquote(value));

/**
 * Converts text to the form it requires as a value in a filter. Backslashes are escaped, internal quotes (if any) are
 * escaped, and the result is quoted.
 *
 * @param   value    text to convert
 * @returns quoted and escaped filter value
 */
export const textToFilterValue = (value: string): string => quote(escapeBackslash(value));

/**
 * Returns true if the given string is a digit.
 *
 * @param   char    a one-character string
 * @return          true if the given string is a digit
 */
export const isDigit = (char: string): boolean => char.length === 1 && char >= '0' && char <= '9';

/**
 * Compare two strings that may have embedded numbers numerically. For example, the string 'host-10' will be
 * greater than 'host-2'. For comparison purposes, only non-negative integers are considered. Comparing negative
 * numbers and/or floats will not have the expected results.
 *
 * Leading zeros may produce unexpected results since digits will be compared to non-digits. For example, '2-a'
 * will sort before '02-a' because the '-' will be compared to the '2'. However, '2a' will sort after '02a' because
 * 'a' sorts after '2'.
 *
 * NOTE: localeCompare() can do numeric comparison, but it's very slow
 *
 * TODO: Handle leading zeros
 *
 * @returns   -1 if a is less, 1 if a is more, 0 if they are equal
 */
export const numericStringCompare = (a?: string, b?: string): -1 | 0 | 1 => {
  // no value is always less than a value
  if (!a || !b) {
    if (!a && !b) {
      return 0;
    }
    return !a ? -1 : 1;
  }

  const a1 = a.toLowerCase();
  const b1 = b.toLowerCase();

  const len = Math.min(a1.length, b1.length);
  for (let i = 0; i < len; i += 1) {
    const charA = a1[i];
    const charB = b1[i];
    if (isDigit(charA) && isDigit(charB)) {
      // convert sequence of digits to number and compare
      const numA = parseInt(a1.substring(i), 10);
      const numB = parseInt(b1.substring(i), 10);
      if (!Number.isNaN(numA) && !Number.isNaN(numB) && numA !== numB) {
        const result = numA - numB;
        if (result > 0) {
          return 1;
        }
        return result < 0 ? -1 : 0;
      }
      // TODO: could optimize a bit by jumping past the number when it's the same in both strings
    } else if (charA !== charB) {
      if (charA > charB) {
        return 1;
      }
      return charA < charB ? -1 : 0;
    }
  }

  if (a1.length > b1.length) {
    return 1;
  }
  return a1.length < b1.length ? -1 : 0;
};

/**
 * Returns the first or top-level identifier in a lodash PropertyPath. A path may be expressed either
 * as a PropertyName or an array of PropertyNames.
 *
 * @see documentation in @types/lodash/common/common.d.ts
 *
 * @param   path    A lodash PropertyPath such as ['items', 0, 'cost'] or 'items[0].cost'
 * @return          The name of the first property
 */
export const getFirstPropertyNameInPropertyPath = (path: PropertyPath): PropertyName => {
  const pathAsArray = isArray(path) ? path : String(path).split(/\.|\[/);
  return pathAsArray[0];
};

/**
 * Return a regex (in string form) version of the given string that handles wildcards (but no other special characters).
 *
 * @param text    a string that can contain wildcards (*)
 * @return a string that can be used to construct a RegExp
 */
export const wildcardRegex = (text: string): string => (text ? '^' + text.replace(/\*/g, '.*') + '$' : '');

/**
 * Return true if the given text contains the given filter string. Wildcards (*) are supported. A wildcard at
 * the end of the filter string will anchor the search text to the beginning. A wildcard at the start of
 * the filter string will anchor the search text to the end. A wildcard in the middle does both.
 * Multiple wildcards are not supported, though a pair of wildcards on either end are removed since
 * they equate to a simple substring search.
 *
 * @param text      the string to search
 * @param filter    the search string to look for in the text
 *
 * @return true if the filter matches the text
 *
 * TODO: handle escaped wildcard; can be done via negative lookbehind, not sure it's worth it
 */
export const wildcardMatch = (text: string, filter: string): boolean => {
  const lcText = text.trim().toLowerCase();
  const lcFilter = filter.toLowerCase();
  if (!filter.includes('*')) {
    return lcText.includes(lcFilter);
  }

  let regex;
  try {
    regex = new RegExp(wildcardRegex(lcFilter));
  } catch (e) {
    // ignore
  }

  return regex ? regex.test(lcText) : lcText.includes(lcFilter);
};

/**
 * Append the plural suffix to a given string
 *
 * @param text    the string to pluralize
 * @param count   Number of items. Use to see if we need to add a suffix
 * @param suffix  Suffix to add. Defaults to 's'
 */
export const pluralize = (text: string, count: number, suffix = 's'): string => {
  if (count === 1) {
    return text;
  }
  return text + suffix;
};

/**
 * Takes what may be a heavily escaped JSON facet value and makes it easier to read by converting the JSON to an object
 * and then returning the string version of that object. That way, numbers and booleans will appear without quotes.
 *
 * @param value    facet value
 * @return formatted value
 */
export const formatFacetValue = (value: string): string => {
  let parsedValue = null;
  let newVal = null;
  try {
    // JSON.parse does not accept single quoted strings
    // check to convert single single to double quotes before parsing
    newVal = value.startsWith("'") && value.endsWith("'") ? '"' + value.substring(1, value.length - 1) + '"' : value;
    parsedValue = newVal ? JSON.parse(newVal) : newVal;
  } catch (e) {
    return unquote(value);
  }

  return String(parsedValue);
};

/**
 * Escape special characters from facet name. Special characters are any that are not a letter, digit, underscore, period,
 * dollar sign, hyphen, or colon. It is assumed that the facet name has not already been escaped. There is no determination
 * as to whether the facet name is valid.
 *
 * @param facetName    name of the facet that we want to escape special characters from
 * @return escaped facet name
 */
export const escapeFacetName = (facetName: string): string => facetName.replace(/[^\w.$:-]/g, '\\$&');

/**
 * Replace escaped characters in a facet name with their unescaped versions (remove the preceding backslash).
 *
 * @param facetName    name of the facet that we want to unescape special characters from
 * @return unescaped facet name
 */
export const unescapeFacetName = (facetName: string): string => facetName.replace(/\\([^\w.$:-])/g, '$1');

/**
 * Truncates text to the given length, replacing elided text with an ellipsis at the chosen position. Text that is not
 * longer than the given length is returned unmodified.
 *
 * @param text              original text
 * @param maxLength         maximum length of returned text
 * @param placement         where to place the ellipsis (default to end)
 * @param ellipsis          string to use as an ellipsis (defaults to '...')
 * @param ellipsisLength    ellipsis may be an element or HTML entity
 */
export const truncateText = (
  text = '',
  maxLength: number,
  placement = K.PLACEMENT.END,
  ellipsis = '...',
  ellipsisLength = 3,
): string => {
  const strText = String(text ?? '');

  if (strText.length <= maxLength) {
    return strText;
  }

  const maxTextLength = maxLength - ellipsisLength;
  switch (placement) {
    case K.PLACEMENT.BEGINNING: {
      return ellipsis + strText.substring(Math.max(0, strText.length - maxTextLength));
    }
    case K.PLACEMENT.MIDDLE: {
      const firstPartLength = Math.floor(maxTextLength / 2);
      const firstPart = strText.substring(0, firstPartLength);
      const secondPart = strText.substring(strText.length - (maxTextLength - firstPartLength));
      return firstPart + ellipsis + secondPart;
    }
    case K.PLACEMENT.END:
    default:
      return strText.substring(0, maxTextLength) + ellipsis;
  }
};

/**
 * Generates a simple string hash that can be used to shorten text. Please don't use this to secure anything,
 * as it's not that kind of hash.
 *
 * See funhash @ https://gist.github.com/iperelivskiy/4110988
 *
 * @param text  the text to shorten
 */
export const generateInsecureHash = (text: string): string => {
  let hex = 0xdeadbeef; // silly old-school initial value
  const knuthValue = 2654435761; // a really big prime number

  for (let i = 0; i < text.length; i += 1) {
    // for loop is the fastest iterator
    hex = Math.imul(hex ^ text.charCodeAt(i), knuthValue); // eslint-disable-line no-bitwise
  }

  const value = (hex ^ (hex >>> 16)) >>> 0; // eslint-disable-line no-bitwise
  return value.toString();
};

/**
 * The config to pass to the unparse function of the papa parse library
 *
 * escapeFormulae	If true, field values that begin with =, +, -, @, \t, or \r,
 * will be prepended with a ' to defend against injection attacks,
 * because Excel and LibreOffice will automatically parse such cells as formulae.
 *
 *
 */
export const csvConfig = {
  escapeFormulae: true,
};

/**
 * Formats the given table data into a CSV string.
 *
 * @param fields    list of column headers
 * @param data      list of data rows
 */
export const formatAsCsvContent = (fields: string[], data: Array<Array<string | number | boolean>>): string =>
  unparse({ fields, data }, csvConfig);

/**
 * Returns the domain portion of an email address. Defaults to returning the last two parts, For example,
 * both "fred@example.com" and "fred@foo.example.com" will return "example.com".
 *
 * Note: this assumes the email has a fairly standard form, and doesn't account for full RFC822 support.
 *
 * @param email    email address
 */
export const getDomainFromEmail = (email: string, numParts = 2): string => {
  const emailParts = email.split('@');
  const domainParts = emailParts.length === 2 ? emailParts[1].split('.') : null;
  if (domainParts && domainParts.length >= numParts) {
    return domainParts.slice(numParts * -1).join('.');
  }

  return emailParts[emailParts.length - 1];
};

/**
 * Removes line breaks from string.
 *
 * @param text string with line breaks or without them.
 */
export const removeLineBreaks = (text: string): string => text.replace(/\n/g, '');

/**
 * Removes excessive GraphQL error message prefix.
 *
 * @param message GraphQL error message.
 */
export const clearGraphQLErrorMessage = (message: string): string => message.replace('GraphQL error: ', '');

/**
 * Manually convert an array of strings from its stringified form in a URL to a real array. We could just use
 * JSON.parse, but that would open up a possible security hole.
 *
 * @param value          value of a URL param
 * @param returnArray    if true, return an array value
 *
 * @returns array of strings, or the value if it is not an array
 */
export const resolveParamValue = (value: string, returnArray = false): string | string[] => {
  let result: string | string[] = value;
  if (returnArray) {
    if (value[0] === '[' && value[value.length - 1] === ']') {
      result =
        value.length > 2
          ? value
              .substring(1, value.length - 1)
              .split(',')
              .map((val) => unquote(val))
          : [];
    } else {
      result = value.split(',');
    }
  }
  return result;
};

// Remove initial $ from field name if present. It's no longer required or useful.
export const stripDollar = (field: string): string => (field.startsWith('$') ? field.substring(1) : field);

/**
 * Return true if the given string could be JSON, including if it looks like a JSON array.
 * The test is very loose because we want to recognize partial (truncated) JSON.
 *
 * @param str    a string
 * @returns true if the string looks like JSON
 */
export const looksLikeJson = (str: string): boolean => str.startsWith('{') || str.startsWith('[');

/**
 *
 * Check whether the passed string is a valid email address or not.
 *
 * @param email
 * @returns true if the passed email argument is of valid format
 */
export const validEmailFormat = (email: string): boolean =>
  // Using yup email validation
  string().email().isValidSync(email);
